import { createListCollection, HStack, Icon, Text } from '@chakra-ui/react';
import { CustomSelect, Option } from '@components/ui/select/Select';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveOrganization } from '@redux/authent/authent.selectors';
import { useSetManualTargetClassificationMutation } from '@services/c2/track.api';
import { getClassificationIcon, getTargetClassification, resolveClassification } from '@utils/target.utils';
import { isEqual } from 'lodash';
import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TargetStatusEnum } from '@/types/c2/c2.types';
import { Classification, ClassificationEnum } from '@/types/sensor/classification.types';
import { TrackLight } from '@/types/sensor/sensors';

type Props = {
  classification: Classification;
  site: string;
  trackIds: TrackLight[];
  status: TargetStatusEnum;
  isDisabled?: boolean;
};

const AUTOMATIC = 'AUTOMATIC';

function ClassificationSelect({ classification, site, trackIds, status, isDisabled = false }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [updateManualClassification] = useSetManualTargetClassificationMutation();
  const manualClassificationList = [
    ClassificationEnum.FRIEND,
    ClassificationEnum.UNKNOWN,
    ClassificationEnum.WATCH,
    ClassificationEnum.SUSPECT,
    ClassificationEnum.ENEMY,
  ];

  const organization = useAppSelector(selectActiveOrganization);
  const currentClassification = getTargetClassification({ classification }, organization);
  const computedClassification = resolveClassification(classification.classification, organization);
  const automaticClassification = computedClassification ?? ClassificationEnum.UNKNOWN;
  const currentValue = computedClassification ? currentClassification : AUTOMATIC;

  const classificationOptions = [
    {
      value: AUTOMATIC,
      label: formatMessage({
        id: `target.classification.${automaticClassification}`,
      }),
    },
  ].concat(
    manualClassificationList.map((classification) => {
      return {
        value: classification.valueOf(),
        label: formatMessage({ id: `target.classification.${classification.valueOf()}` }),
      };
    }),
  );

  const formatLabel = (option: Option) => (
    <HStack gap={1.5} width="100%" overflow="hidden" textOverflow="ellipsis">
      <Icon
        boxSize="24px"
        as={getClassificationIcon(
          option.value === AUTOMATIC ? automaticClassification : (option.value as ClassificationEnum),
        )}
      />
      <Text textStyle="screenStd" color="neutral.white">
        {option.label}
      </Text>
      <Text
        textStyle="screenStd"
        color="neutral.200"
        marginLeft="auto"
        lineClamp={1}
        textOverflow="ellipsis"
        overflow="hidden"
        minWidth={0}
      >
        <FormattedMessage id={`target.${option.value === AUTOMATIC ? 'auto' : 'manual'}`} />
      </Text>
    </HStack>
  );

  return (
    <CustomSelect
      value={currentValue}
      collection={createListCollection({ items: classificationOptions })}
      disabled={isDisabled || status === TargetStatusEnum.LOST}
      width="100%"
      listWidth="100%"
      size="sm"
      formatLabel={formatLabel}
      onChange={(selectedOption) =>
        selectedOption &&
        updateManualClassification({
          tracks: trackIds,
          site: site,
          classificationEnum: selectedOption === 'AUTOMATIC' ? null : (selectedOption as ClassificationEnum),
        })
      }
    />
  );
}

export default memo(ClassificationSelect, isEqual);
